<!--
 * @Description: 运维巡检记录
-->
<template>
  <scroll-layout class="inspection-report">
    <template slot="header">
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @clean="handleSearchClean"
        @click="handleSearchConfirm"></ykc-search>
    </template>
    <ykc-table
      ref="YkcTable"
      operateFixedType="right"
      :data="tableData"
      title="运维巡检记录"
      :columns="tableColumns"
      :operateButtons="tableOperateButtons">
      <template #headerButtons>
        <div class="table-header-button">
          <ykc-button
            v-rbac="'operation:inspectionReport:export'"
            type="plain"
            @click="doExportFile">
            导出
          </ykc-button>
        </div>
      </template>

      <ykc-pagination
        align="right"
        slot="pagination"
        ref="YkcPagination"
        :total="pageInfo.total"
        :page-size.sync="pageInfo.size"
        :current-page.sync="pageInfo.current"
        @size-change="handlePaginationSizeChange"
        @current-change="handlePaginationCurrentChange"></ykc-pagination>
    </ykc-table>

    <!-- 详情弹窗 -->
    <el-dialog
      title="巡检记录详情"
      :visible.sync="detailVisible"
      width="864px"
      :append-to-body="true"
      custom-class="inspection-detail-dialog">
      <div v-if="detailLoading" class="detail-loading">
        <i class="el-icon-loading"></i>
        加载中...
      </div>
      <div v-else-if="detailData" class="detail-content">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="detail-section-title">基本信息</div>
          <div class="detail-info-grid">
            <div class="detail-info-item">
              <span class="detail-info-label">巡检记录编码</span>
              <span class="detail-info-value">{{ detailData.recordNo || '——' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">巡检人</span>
              <span class="detail-info-value">{{ detailData.userName || '--' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">所属平台</span>
              <span class="detail-info-value">{{ detailData.platformName || '淘淘充' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">电站名称</span>
              <span class="detail-info-value">{{ detailData.stationName || '--' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">电站类型</span>
              <span class="detail-info-value">{{ detailData.stationTypeText || '——' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">巡检开始时间</span>
              <span class="detail-info-value">{{ detailData.createTime || '——' }}</span>
            </div>
            <div class="detail-info-item">
              <span class="detail-info-label">巡检完成时间</span>
              <span class="detail-info-value">{{ detailData.finishTime || '——' }}</span>
            </div>
          </div>
        </div>

        <!-- 现场人脸打卡照片 -->
        <div class="detail-section" v-if="detailData.images && detailData.images.length">
          <div class="detail-section-title">现场人脸打卡照片</div>
          <div class="detail-images">
            <el-image
              v-for="(img, index) in detailData.images"
              :key="index"
              :src="img"
              :preview-src-list="detailData.images"
              fit="cover"
              class="detail-image-item"></el-image>
          </div>
        </div>

        <!-- 现场签字打卡照 -->
        <div
          class="detail-section"
          v-if="detailData.signPhotoUrls && detailData.signPhotoUrls.length">
          <div class="detail-section-title">现场签字打卡照</div>
          <div class="detail-images">
            <el-image
              v-for="(img, index) in detailData.signPhotoUrls"
              :key="index"
              :src="img"
              :preview-src-list="detailData.signPhotoUrls"
              fit="cover"
              class="detail-image-item"></el-image>
          </div>
        </div>

        <!-- 巡检项结果 -->
        <div class="detail-section" v-if="detailData.items && detailData.items.length">
          <div class="detail-section-title">巡检结果（{{ detailData.items.length }}项）</div>
          <el-table :data="detailData.items" border size="small" class="detail-items-table">
            <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
            <el-table-column prop="category" label="分类" width="120"></el-table-column>
            <el-table-column prop="itemName" label="巡检项目" min-width="180"></el-table-column>
            <el-table-column prop="resultText" label="结果" width="100" align="center">
              <template slot-scope="{ row }">
                <span :class="getResultClass(row.result)">{{ row.resultText }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="recordContent" label="巡检记录" min-width="150">
              <template slot-scope="{ row }">
                {{ row.recordContent || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="巡检图片" min-width="200">
              <template slot-scope="{ row }">
                <div v-if="row.photos && row.photos.length" class="detail-row-images">
                  <el-image
                    v-for="(img, index) in row.photos"
                    :key="index"
                    :src="img"
                    :preview-src-list="row.photos"
                    fit="cover"
                    class="detail-row-image"></el-image>
                </div>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="150">
              <template slot-scope="{ row }">
                {{ row.remark || '--' }}
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 自定义巡检项 -->
        <div class="detail-section" v-if="detailData.customItems && detailData.customItems.length">
          <div class="detail-section-title">
            自定义巡检项（{{ detailData.customItems.length }}项）
          </div>
          <el-table :data="detailData.customItems" border size="small" class="detail-items-table">
            <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
            <el-table-column prop="content" label="巡检内容" min-width="200"></el-table-column>
            <el-table-column prop="remark" label="备注" min-width="150">
              <template slot-scope="{ row }">
                {{ row.remark || '——' }}
              </template>
            </el-table-column>
            <el-table-column prop="creatorName" label="添加人" width="120"></el-table-column>
          </el-table>
        </div>
      </div>
      <div v-else class="detail-empty">暂无数据</div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </scroll-layout>
</template>

<script>
  import { inspectionReportApi } from '@/service/apis';
  import { offlineExport } from '@/utils';

  export default {
    name: 'InspectionReport',
    data() {
      return {
        tableData: [],
        pageInfo: {
          current: 1,
          size: 10,
          total: 0,
        },
        searchParams: {
          recordNo: '',
          userName: '',
          stationName: '',
          platform: '',
          startDate: '',
          endDate: '',
        },
        // 详情弹窗
        detailVisible: false,
        detailLoading: false,
        detailData: null,
      };
    },
    computed: {
      searchData() {
        return [
          {
            comName: 'YkcInput',
            key: 'recordNo',
            label: '巡检记录编码',
            placeholder: '请输入编码',
          },
          {
            comName: 'YkcInput',
            key: 'userName',
            label: '巡检人',
            placeholder: '请输入巡检人',
          },
          {
            comName: 'YkcDropdown',
            key: 'platform',
            label: '所属平台',
            placeholder: '请选择所属平台',
            data: [
              { id: 1, name: '淘淘充' },
              { id: 2, name: '益虫' },
            ],
          },
          {
            comName: 'YkcInput',
            key: 'stationName',
            label: '电站名称',
            placeholder: '请输入电站名称',
          },
          {
            comName: 'YkcDatePicker',
            key: 'dateRange',
            label: '巡检开始时间',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
            type: 'daterange',
            valueFormat: 'yyyy-MM-dd',
          },
          {
            comName: 'YkcDatePicker',
            key: 'finishDateRange',
            label: '巡检完成时间',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
            type: 'daterange',
            valueFormat: 'yyyy-MM-dd',
          },
        ];
      },
      tableColumns() {
        return [
          { label: '巡检记录编码', prop: 'recordNo', minWidth: '160px' },
          { label: '巡检人', prop: 'userName', minWidth: '100px' },
          {
            label: '所属平台',
            prop: 'platformName',
            minWidth: '100px',
            formatter: row => row.platformName || '--',
          },
          { label: '电站名称', prop: 'stationName', minWidth: '180px' },
          {
            label: '电站类型',
            prop: 'stationTypeText',
            minWidth: '100px',
            formatter: row => {
              if (row.stationType === 1) return '公桩';
              if (row.stationType === 2) return '私桩';
              return '——';
            },
          },
          {
            label: '合格数',
            prop: 'qualifiedCount',
            minWidth: '90px',
            align: 'center',
            formatter: row => {
              const count = row.qualifiedCount || 0;
              return count > 0
                ? `<span style="color:#38a169;font-weight:500">${count}</span>`
                : count;
            },
          },
          {
            label: '不合格数',
            prop: 'unqualifiedCount',
            minWidth: '90px',
            align: 'center',
            formatter: row => {
              const count = row.unqualifiedCount || 0;
              return count > 0
                ? `<span style="color:#e53e3e;font-weight:500">${count}</span>`
                : count;
            },
          },
          {
            label: '不适用数',
            prop: 'notApplicableCount',
            minWidth: '90px',
            align: 'center',
          },
          { label: '巡检开始时间', prop: 'createTime', minWidth: '150px' },
          { label: '巡检完成时间', prop: 'finishTime', minWidth: '150px' },
        ];
      },
      tableOperateButtons() {
        return [
          {
            id: '1',
            text: '详情',
            handleClick: (btn, { row }) => {
              this.handleDetail(row);
            },
          },
          {
            id: '2',
            text: '记录导出',
            handleClick: (btn, { row }) => {
              this.handleExportPdf(row);
            },
          },
        ];
      },
    },
    created() {
      this.requestList();
    },
    methods: {
      /** 查询列表 */
      requestList() {
        const params = {
          ...this.searchParams,
          pageNum: this.pageInfo.current,
          pageSize: this.pageInfo.size,
        };
        inspectionReportApi.getList(params).then(res => {
          /* eslint-disable prefer-destructuring */
          this.tableData = (res && res.records) || [];
          this.pageInfo.total = (res && res.total) || 0;
          /* eslint-enable prefer-destructuring */
        });
      },
      /** 搜索确认 */
      handleSearchConfirm(data) {
        if (data) {
          if (data.dateRange && data.dateRange.length === 2) {
            this.searchParams.startDate = data.dateRange[0];
            this.searchParams.endDate = data.dateRange[1];
          } else {
            this.searchParams.startDate = '';
            this.searchParams.endDate = '';
          }
          if (data.finishDateRange && data.finishDateRange.length === 2) {
            this.searchParams.finishStartDate = data.finishDateRange[0];
            this.searchParams.finishEndDate = data.finishDateRange[1];
          } else {
            this.searchParams.finishStartDate = '';
            this.searchParams.finishEndDate = '';
          }
          this.searchParams.recordNo = data.recordNo || '';
          this.searchParams.userName = data.userName || '';
          this.searchParams.stationName = data.stationName || '';
          this.searchParams.platform = data.platform || '';
        }
        this.pageInfo.current = 1;
        this.requestList();
      },
      /** 搜索重置 */
      handleSearchClean() {
        this.searchParams = {
          recordNo: '',
          userName: '',
          stationName: '',
          platform: '',
          startDate: '',
          endDate: '',
          finishStartDate: '',
          finishEndDate: '',
        };
        this.pageInfo.current = 1;
        this.requestList();
      },
      /** 分页 */
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.requestList();
      },
      handlePaginationSizeChange(size) {
        this.pageInfo.size = size;
        this.pageInfo.current = 1;
        this.requestList();
      },
      /** 查看详情 */
      handleDetail(row) {
        this.detailVisible = true;
        this.detailLoading = true;
        this.detailData = null;
        inspectionReportApi
          .getDetail(row.id)
          .then(res => {
            this.detailData = res;
          })
          .catch(err => {
            console.error('获取巡检详情失败', err);
            this.detailData = null;
          })
          .finally(() => {
            this.detailLoading = false;
          });
      },
      /** 巡检结果样式 */
      getResultClass(result) {
        switch (result) {
          case 1:
            return 'result-qualified';
          case 2:
            return 'result-unqualified';
          case 3:
            return 'result-not-applicable';
          default:
            return '';
        }
      },
      /** 导出 */
      doExportFile() {
        offlineExport(
          {
            downloadType: 'inspection_report_export',
            jsonNode: {
              ...this.searchParams,
            },
          },
          this.pageInfo.total
        );
      },
      /** 导出巡检记录PDF */
      handleExportPdf(row) {
        inspectionReportApi
          .exportPdf(row.id)
          .then(res => {
            if (res) {
              const link = document.createElement('a');
              link.href = `data:application/pdf;base64,${res}`;
              link.download = `巡检记录_${row.recordNo || row.id}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          })
          .catch(err => {
            console.error('导出PDF失败', err);
            this.$message.error('导出PDF失败，请重试');
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .inspection-detail-dialog {
    .detail-loading,
    .detail-empty {
      text-align: center;
      padding: 40px 0;
      color: #9ca3af;
      font-size: 14px;
    }

    .detail-content {
      max-height: 65vh;
      overflow-y: auto;
    }

    .detail-section {
      margin-bottom: 24px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .detail-section-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 12px;
      padding-left: 10px;
      border-left: 3px solid #4d8cfd;
    }

    .detail-info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px 24px;
    }

    .detail-info-item {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 1.6;
    }

    .detail-info-label {
      color: #6b7280;
      width: 110px;
      flex-shrink: 0;
    }

    .detail-info-value {
      color: #1f2937;
    }

    .detail-images {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .detail-image-item {
      width: 80px;
      height: 80px;
      border-radius: 4px;
      border: 1px solid #e5e7eb;
      cursor: pointer;
    }

    .detail-row-images {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .detail-row-image {
      width: 50px;
      height: 50px;
      border-radius: 4px;
      border: 1px solid #e5e7eb;
      cursor: pointer;
    }

    .detail-items-table {
      width: 100%;
    }

    .result-qualified {
      color: #38a169;
      font-weight: 500;
    }

    .result-unqualified {
      color: #e53e3e;
      font-weight: 500;
    }

    .result-not-applicable {
      color: #a0aec0;
    }
  }
</style>
